import React, { useState, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import map from 'lodash/map';
import filter from 'lodash/filter';
import includes from 'lodash/includes';

import Checkbox from '../Checkbox';
import DropDown from '../Dropdown';
import Icon from '../Icon';
import Button from '../Button';
import Input from '../Input';
import Item from '../Item';
import { useDefault } from '../../common/hooks';
import { nfn } from '../../common';
import closeIcon from '../DropdownInput/images/close.svg';
import arrowIcon from './images/arrow-down.svg';
import linkCloseIcon from '../Input/images/clear.svg';

const { CheckboxGroup } = Checkbox;

const Overlay = props => {
  const {
    options,
    onChange,
    onClear,
    onEnsure,
    defaultValue,
    withSearch,
    mini,
    sortable,
    searchTxt,
    withFooter,
    checkboxType,
    isSingle,
    selectHeight,
    onClose,
    onSearch,
    placeholder
  } = props;

  const getResultOptions = () => {
    const filtered =
      searchTxt === ''
        ? options
        : filter(options, item => includes(item.label, searchTxt));

    if (!sortable) return filtered;

    // 如果可排序，将已选中的放到前面

    const selected = filtered.filter(item =>
      includes(defaultValue, item.value)
    );
    const unSelected = filtered.filter(
      item => !includes(defaultValue, item.value)
    );

    return selected.concat(unSelected);
  };

  const selectSingleVal = val => {
    onChange([val]);
    onClose();
  };

  const resultOptions = useMemo(getResultOptions, [
    searchTxt,
    options,
    sortable,
    defaultValue
  ]);

  const cls = classNames('checkbox-select-content', {
    sortable
  });

  return (
    <div className={cls}>
      <Item show={withSearch}>
        <Input
          mini={mini}
          placeholder={placeholder}
          isSearch={true}
          onChange={onSearch}
          className="checkbox-select-search-input"
        />
      </Item>
      <div
        className="checkbox-select-content-checkbox"
        style={{ maxHeight: selectHeight + 'px' || 'auto' }}
      >
        <Item show={isSingle}>
          <ul>
            {map(resultOptions, (item, index) => (
              <li
                className={classNames('checkbox-label', { mini: mini })}
                onClick={() => selectSingleVal(item.value, onEnsure)}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </Item>
        <Item show={!isSingle}>
          <CheckboxGroup defaultValue={defaultValue} onChange={onChange}>
            {map(resultOptions, (item, index) => (
              <Checkbox
                key={`${item.label}-${index}`}
                type={checkboxType}
                mini={mini}
                label={item.label}
                value={item.value}
              />
            ))}
          </CheckboxGroup>
        </Item>
      </div>
      <Item show={withFooter}>
        <div className="checkbox-select-button-wrap">
          <Button type="white" mini={true} className="cancel" onClick={onClear}>
            清除
          </Button>
          <Button
            type="primary"
            className="confirm"
            onClick={onEnsure}
            mini={true}
          >
            确定
          </Button>
        </div>
      </Item>
    </div>
  );
};
function CheckboxSelect({
  defaultValue,
  defaultOpen,
  withSearch,
  withFooter,
  onChange,
  onClear,
  title,
  sortable,
  stopPropagation,
  mini,
  options,
  isLink,
  isSingle,
  emptyLabel,
  onDelete,
  className,
  style,
  checkboxType,
  inline,
  getContainer,
  selectHeight,
  selectWidth,
  onOk,
  background,
  getValueString,
  disabled
}) {
  const [value, setValue] = useDefault(defaultValue);
  const [searchTxt, setSearchTxt] = useState('');
  const [visible, setVisible] = useState(defaultOpen);

  const handleEnsure = useCallback(() => {
    onChange(value);
    setVisible(false);
    onOk(value);
  }, [value, onChange]);

  const handleSearch = useCallback(val => setSearchTxt(val), []);

  const handleSelectChange = useCallback(
    v => {
      setValue(v);
      onChange(v);
    },
    [setValue, onChange]
  );

  const handleClear = useCallback(() => {
    handleSelectChange([]);
    onClear([]);
  }, [onChange, onClear]);

  const valueStr = getValueString
    ? getValueString(value, options)
    : filter(options, item => value.includes(item.value))
        .map(item => item.label)
        .join(',');

  const classes = classNames(
    'checkbox-select-wrap',
    { link: isLink },
    onDelete ? className + ' can-delete' : className
  );
  const resultClasses = classNames('checkbox-select-result', 'radius', {
    background,
    mini,
    disabled
  });
  return (
    <div className={classes}>
      <DropDown
        visible={visible}
        onVisibleChange={setVisible}
        inline={inline}
        disabled={disabled}
        stopPropagation={stopPropagation}
        maxWidth={selectWidth}
        overlay={
          <Overlay
            sortable={sortable}
            options={options}
            title={title}
            defaultValue={value}
            isSingle={isSingle}
            mini={mini}
            selectHeight={selectHeight}
            checkboxType={checkboxType}
            withSearch={withSearch}
            withFooter={withFooter}
            searchTxt={searchTxt}
            onSearch={handleSearch}
            onEnsure={handleEnsure}
            onClose={() => setVisible(false)}
            onClear={handleClear}
            onChange={handleSelectChange}
          />
        }
        defaultOpen={defaultOpen}
        getContainer={getContainer}
      >
        <div className={resultClasses}>
          <Item show={title}>
            <span className="checkbox-select-result-label">{title}</span>
          </Item>
          <p className="checkbox-select-result-value" title={valueStr}>
            {valueStr || emptyLabel}
          </p>
          <div className="arrow-zone">
            <Icon link={arrowIcon} className="drop-down-icon" />
          </div>
          <Item show={onDelete && !isLink}>
            <Icon className="close-icon" link={closeIcon} onClick={onDelete} />
          </Item>
        </div>
      </DropDown>
      <Item show={onDelete && isLink}>
        <Icon className="close-icon" link={linkCloseIcon} onClick={onDelete} />
      </Item>
    </div>
  );
}

CheckboxSelect.propTypes = {
  /** 选项列表，包含label, value字段 */
  options: PropTypes.array,
  /** 标题 */
  title: PropTypes.string,
  /** 默认值 */
  defaultValue: PropTypes.array,
  /** 是否默认展开下拉选项*/
  defaultOpen: PropTypes.bool,
  /** 已选中的默认放到前面 */
  sortable: PropTypes.bool,
  /** change回调事件 */
  onChange: PropTypes.func,
  /** 清除的回调事件 */
  onClear: PropTypes.func,
  /** 确定回调事件 */
  onOk: PropTypes.func,
  /** 映射选中值的字符串 */
  getValueString: PropTypes.func,
  /** 小样式 */
  mini: PropTypes.bool,
  /** 是否带背景 */
  background: PropTypes.bool,
  /** 是否是link样式 */
  isLink: PropTypes.bool,
  /** 支持搜索 */
  withSearch: PropTypes.bool,
  /** 是否包含底部按钮 */
  withFooter: PropTypes.bool,
  /** 删除回调 */
  onDelete: PropTypes.func,
  /** 是否阻止冒泡 */
  stopPropagation: PropTypes.bool,
  /** 自定义class */
  className: PropTypes.string,
  /** checkbox 类型 */
  checkboxType: PropTypes.oneOf(['safe', 'normal', 'red']),
  /** isSingle 是否单选 */
  isSingle: PropTypes.bool,
  /** 空提示 */
  emptyLabel: PropTypes.string,
  /** 自定义样式 */
  style: PropTypes.object,
  /** 下拉选项是否采用portal, 同Dropdown属性*/
  inline: PropTypes.bool,
  /** 下拉选项定位参照物，同Dropdown属性*/
  getContainer: PropTypes.func,
  /** 选择区域的高度 */
  selectHeight: PropTypes.number,
  /** 选择区域的宽度 */
  selectWidth: PropTypes.number,
  /** 是否禁用 */
  disabled: PropTypes.bool
};

CheckboxSelect.defaultProps = {
  withSearch: false,
  onDelete: null,
  isLink: false,
  isSingle: false,
  checkboxType: 'normal',
  mini: false,
  sortable: false,
  background: false,
  withFooter: false,
  stopPropagation: true,
  emptyLabel: '',
  onClear: nfn,
  onChange: nfn,
  onOk: nfn,
  defaultValue: [],
  disabled: false
};
export default CheckboxSelect;
